import { get, set, del, keys } from 'idb-keyval';

/**
 * Wrapper de persistance basé sur IndexedDB (idb-keyval).
 *
 * Migration depuis localStorage : au premier appel, scanne les clés `apollo:*`
 * éventuellement présentes dans localStorage et les transfère vers IDB.
 * Les valeurs étaient sérialisées en JSON par l'ancienne implémentation.
 */

const APOLLO_PREFIX = 'apollo:';
const MIGRATION_FLAG = 'apollo:__migrated_from_localstorage__';

let migrationPromise: Promise<void> | null = null;

const migrateFromLocalStorageOnce = async (): Promise<void> => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  const flag = await get(MIGRATION_FLAG).catch(() => null);
  if (flag === true) return;

  const lsKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(APOLLO_PREFIX)) lsKeys.push(k);
  }

  for (const k of lsKeys) {
    try {
      const raw = localStorage.getItem(k);
      if (raw == null) continue;
      let value: unknown;
      try { value = JSON.parse(raw); } catch { value = raw; }
      const existing = await get(k);
      if (existing === undefined) {
        await set(k, value);
      }
      localStorage.removeItem(k);
    } catch (err) {
      console.warn('[storage] migration localStorage échec sur', k, err);
    }
  }

  await set(MIGRATION_FLAG, true);
};

const ensureMigrated = (): Promise<void> => {
  if (!migrationPromise) migrationPromise = migrateFromLocalStorageOnce();
  return migrationPromise;
};

/** Lit une clé. Retourne `defaultValue` si absente. */
export const storageGet = async <T>(key: string, defaultValue: T): Promise<T> => {
  await ensureMigrated();
  try {
    const v = await get<T>(key);
    return v === undefined ? defaultValue : v;
  } catch (err) {
    console.error('[storage] get échec', key, err);
    return defaultValue;
  }
};

/** Écrit une clé. Retourne true si OK. */
export const storageSet = async <T>(key: string, value: T): Promise<boolean> => {
  await ensureMigrated();
  try {
    await set(key, value);
    return true;
  } catch (err) {
    console.error('[storage] set échec', key, err);
    return false;
  }
};

/** Supprime une clé. */
export const storageDelete = async (key: string): Promise<boolean> => {
  await ensureMigrated();
  try {
    await del(key);
    return true;
  } catch (err) {
    console.error('[storage] delete échec', key, err);
    return false;
  }
};

/**
 * Supprime toutes les clés `apollo:*` (utilisé par le reset profil).
 * Conserve le flag de migration.
 */
export const storageClearApollo = async (): Promise<void> => {
  await ensureMigrated();
  const allKeys = await keys();
  for (const k of allKeys) {
    if (typeof k === 'string' && k.startsWith(APOLLO_PREFIX) && k !== MIGRATION_FLAG) {
      await del(k);
    }
  }
};

/** Liste toutes les clés `apollo:*` (utile pour debug/export futur) */
export const storageListApolloKeys = async (): Promise<string[]> => {
  await ensureMigrated();
  const allKeys = await keys();
  return allKeys
    .filter((k): k is string => typeof k === 'string' && k.startsWith(APOLLO_PREFIX) && k !== MIGRATION_FLAG);
};

// Clés canoniques utilisées dans l'app
export const KEYS = {
  profile: 'apollo:profile',
  measurements: 'apollo:measurements',
  recipes: 'apollo:recipes',
  shopping: 'apollo:shopping',
  motivTracks: 'apollo:motivTracks',
  workoutHistory: 'apollo:workoutHistory',
  /** Log quotidien — préfixe à compléter avec todayKey() */
  dayPrefix: 'apollo:day:',
  tipPrefix: 'apollo:tip:',
  coachPrefix: 'apollo:coach:',
} as const;
